'use client';

// Request a password-reset link. Posts to the auth BFF; the backend always
// returns 200 (no account enumeration), so we show the same "check your email"
// confirmation whether or not the address has an account.

import { useState, type FormEvent } from 'react';
import Link from 'next/link';
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import { track } from '@/lib/analytics';
import { brandButtonClass, brandTextLinkClass } from '@/components/brand/BrandButton';
import { brandFieldClass, brandFieldErrorClass, brandFieldLabelClass } from '@/components/brand/BrandField';

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    track('password_reset_requested');
    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const detail = await res.json().then((d) => (d as { detail?: unknown })?.detail).catch(() => null);
        setError(
          res.status === 429
            ? 'Too many requests. Please wait a few minutes and try again.'
            : typeof detail === 'string' && detail.trim()
              ? detail
              : 'Something went wrong. Please try again.',
        );
        setPending(false);
        return;
      }
      setSent(true);
    } catch {
      setError('Something went wrong. Please check your connection and try again.');
    }
    setPending(false);
  }

  return (
    <div className="w-full">
      <h1 className="ss-title text-center">Reset your password</h1>
      <p className="ss-small mt-2 text-center text-ss-slate">
        We&apos;ll email you a link to choose a new one.
      </p>

      <div className="mt-6 rounded-ss-l bg-ss-surface p-6 shadow-ss-edge">
        {sent ? (
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            <MailCheck className="h-7 w-7 text-ss-ideal-ink" />
            <p className="ss-body">
              If an account exists for <span className="font-bold">{email}</span>, a reset
              link is on its way.
            </p>
            <p className="ss-small text-ss-slate">
              The link expires in 1 hour. Check your spam folder if it doesn&apos;t arrive.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3.5">
            <div>
              <label htmlFor="email" className={brandFieldLabelClass}>
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={brandFieldClass}
                placeholder="you@example.com"
              />
            </div>

            {error && (
              <p className={brandFieldErrorClass} role="alert">
                {error}
              </p>
            )}

            <button type="submit" disabled={pending} className={`${brandButtonClass({ variant: 'primary', size: 'lg' })} w-full`}>
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
            </button>
          </form>
        )}
      </div>

      <p className="ss-small mt-4 text-center text-ss-slate">
        <Link href="/login" className={`inline-flex items-center gap-1 ${brandTextLinkClass}`}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>
      </p>
    </div>
  );
}
